import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import { 
  Container, 
  Box, 
  Typography, 
  Button, 
  Alert,
  Paper,
  CircularProgress 
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { Email, CheckCircle, Refresh } from '@mui/icons-material';

const StyledPaper = styled(motion.div)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  background: 'rgba(255, 255, 255, 0.98)',
  borderRadius: '24px',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
  backdropFilter: 'blur(20px)',
  maxWidth: '500px',
  margin: '0 auto',
  textAlign: 'center',
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  '& .MuiSvgIcon-root': {
    fontSize: '2.5rem',
    color: 'white',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 3),
  borderRadius: '12px',
  fontSize: '1rem',
  textTransform: 'none',
  marginTop: theme.spacing(2),
  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
  boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
  '&:hover': {
    background: 'linear-gradient(45deg, #1976D2 30%, #21CBF3 90%)',
  },
  '&:disabled': {
    background: '#ccc',
    boxShadow: 'none',
  },
}));

const EmailVerification = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get email from location state or localStorage
  const email = location.state?.email || localStorage.getItem('pendingVerificationEmail') || '';

  const handleResendEmail = async () => {
    if (!email) {
      setError('No email address found. Please try signing up again.');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });

      if (error) {
        setError(error.message);
      } else {
        setMessage('Verification email sent successfully! Please check your inbox.');
      }
    } catch (err) {
      setError('Failed to resend verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    // Clear the pending email
    localStorage.removeItem('pendingVerificationEmail');
    navigate('/login');
  };

  return (
    <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', py: 4 }}>
      <StyledPaper
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <IconContainer>
          <Email />
        </IconContainer>

        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: '#333' }}>
          Please Verify Your Email
        </Typography>

        <Typography variant="body1" sx={{ mb: 3, color: '#666', lineHeight: 1.6 }}>
          We've sent a verification email to:
        </Typography>

        <Typography variant="h6" sx={{ mb: 3, color: '#2196F3', fontWeight: 'bold' }}>
          {email}
        </Typography>

        <Typography variant="body2" sx={{ mb: 4, color: '#666', lineHeight: 1.6 }}>
          Please click the verification link in your email to activate your account. 
          If you don't see the email, check your spam folder.
        </Typography>

        {message && (
          <Alert severity="success" sx={{ mb: 2, width: '100%' }}>
            {message}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <StyledButton
            variant="contained"
            onClick={handleResendEmail}
            disabled={loading || !email}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
          >
            {loading ? 'Sending...' : 'Resend Verification Email'}
          </StyledButton>

          <Button
            variant="text"
            onClick={handleBackToLogin}
            sx={{ textTransform: 'none', color: '#666' }}
          >
            Back to Login
          </Button>
        </Box>

        <Box sx={{ mt: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 2, width: '100%' }}>
          <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle sx={{ fontSize: '1rem', color: '#4CAF50' }} />
            After verification, you'll be able to complete your wellness quiz and start your journey.
          </Typography>
        </Box>
      </StyledPaper>
    </Container>
  );
};

export default EmailVerification;
